import type {ChangeEvent} from 'react';

import {media, spacing} from '@nfq/react-grid';
import styled from 'styled-components';

import {RadioButton} from './RadioButton';

import type {WithChildren} from 'types/global';


export interface RadioGroupItem {
    /** The label for the item. Can either be a valid `ReactNode` or a function returning a `ReactNode`. */
    label: WithChildren['children'] | ((value: string) => WithChildren['children']);
    /** The value of the item. */
    value: string;
}

interface ComponentProps {
    /**
     * The value of the radio button that is currently checked.
     */
    checked: string | undefined;
    /**
     * An array of items to be displayed as radio options.
     */
    items: RadioGroupItem[];
    /**
     * The label for the radio group.
     */
    label: string;
    /**
     * The name attribute for the radio group.
     */
    name: string;
    /**
     * A callback function invoked when the selected value changes.
     *
     * @param value The new selected value.
     */
    onChange(value: string): void;
    /**
     * Indicates whether the input group is required.
     */
    required?: boolean;
    /**
     * Indicates whether the items should stretch the full width.
     */
    stretch?: boolean;
}

/**
 * Renders a radio group component with customizable options.
 * This component allows users to select a single option from a list of radio buttons.
 *
 * @param props          The component props.
 * @param props.checked  The value of the radio button that is currently checked.
 * @param props.items    An array of items to be displayed as radio options.
 * @param props.label    The label for the radio group.
 * @param props.name     The name attribute for the radio group.
 * @param props.onChange A callback function invoked when the selected value changes.
 * @param props.stretch  Indicates whether the group items should stretch to fill the available space.
 * @param props.required Indicates whether the radio group is required.
 * @returns The JSX element representing the radio group.
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *     const handleChange = (value: string) => {
 *         console.log('Selected value:', value);
 *     };
 *
 *     return (
 *         <RadioGroup
 *             checked="option1"
 *             items={[
 *                 { label: "Option 1", value: "option1" },
 *                 { label: "Option 2", value: "option2" },
 *                 { label: "Option 3", value: "option3" }
 *             ]}
 *             label="Select an option"
 *             name="options"
 *             onChange={handleChange}
 *         />
 *     );
 * };
 * ```
 */
export const RadioGroup = ({checked, items, label, name, onChange, required, stretch}: ComponentProps) => {
    /**
     * Handles the change event when a radio button is selected.
     * This function invokes the onChange callback with the selected value.
     *
     * @param e The change event object.
     */
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        onChange(e.target.value);
    };

    return (
        <Fieldset>
            <Legend>{label}</Legend>
            <Wrapper>
                {items.map(item => {
                    let itemLabel = item.label;

                    if (typeof itemLabel === 'function') {
                        itemLabel = itemLabel(item.value);
                    }

                    return (
                        <RadioButton
                            key={item.value}
                            checked={item.value === checked}
                            name={name}
                            required={required}
                            stretch={stretch}
                            value={item.value}
                            onChange={handleChange}
                        >
                            {itemLabel}
                        </RadioButton>
                    );
                })}
            </Wrapper>
        </Fieldset>
    );
};

const Wrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${spacing(3)};
`;

const Fieldset = styled.fieldset`
    border: 0 none;
    margin: 0;
    padding: 0;
`;

const Legend = styled.legend`
    color: ${({theme}) => theme.colors.ultraPageForeground};
    font: 1.6rem / 1.3 ${({theme}) => theme.fonts.Dinot};
    margin-block-end: ${spacing(1)};

    ${media('md')} {
        font-size: 2rem;
        margin-block-end: ${spacing(3)};
    }
`;